import { useEffect, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Upload, Trash2, ImagePlus, Download, FileText, FileSpreadsheet, ChevronDown, ShoppingCart, MessageCircle, Sparkles, ImageIcon, X, Receipt } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { WhatsAppShareButton } from "@/components/WhatsAppShare";
import { Autocomplete } from "@/components/Autocomplete";
import { buildPromoLines } from "@/lib/promociones";
import { POSProductGrid, POSCart } from "@/components/pos/POSBuilder";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuPortal } from "@/components/ui/dropdown-menu";
import OrderFormDialog from "@/pages/orders/OrderFormDialog";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Facturar (Order → Invoice) dialog state
  const [facturarOrder, setFacturarOrder] = useState(null);
  const [facturarLines, setFacturarLines] = useState([]);
  const [facturarIvaPct, setFacturarIvaPct] = useState(13);
  const [facturarObs, setFacturarObs] = useState("");
  const [facturarLoading, setFacturarLoading] = useState(false);
  // Add-line picker state (search box + bonus toggle for the next-added line).
  const [facturarPickerQuery, setFacturarPickerQuery] = useState("");
  const [facturarPickerBonus, setFacturarPickerBonus] = useState(false);

  const openFacturarDialog = (order) => {
    setFacturarOrder(order);
    setFacturarLines((order.lineas || []).map(l => ({
      codigo: l.codigo,
      producto: l.producto,
      cantidad: l.cantidad,
      precio_unitario: l.precio_unitario,
      es_bonificacion: l.es_bonificacion || false,
    })));
    setFacturarIvaPct(13);
    setFacturarObs("");
    setFacturarPickerQuery("");
    setFacturarPickerBonus(false);
  };

  // Add a product to the invoice line set. If the product is already there
  // and the new line isn't a bonus, increment its quantity instead of adding
  // a duplicate row. Bonus lines always create a fresh row so they stay
  // visually separate (price 0).
  const addFacturarLine = (product) => {
    const codigo = product.codigo;
    if (!facturarPickerBonus) {
      const existingIdx = facturarLines.findIndex(l => l.codigo === codigo && !l.es_bonificacion);
      if (existingIdx >= 0) {
        setFacturarLines(prev => prev.map((x, k) =>
          k === existingIdx ? { ...x, cantidad: (parseFloat(x.cantidad) || 0) + 1 } : x,
        ));
        setFacturarPickerQuery("");
        return;
      }
    }
    setFacturarLines(prev => [...prev, {
      codigo,
      producto: product.descripcion || product.nombre || codigo,
      cantidad: 1,
      precio_unitario: facturarPickerBonus ? 0 : (product.precio_venta || 0),
      es_bonificacion: facturarPickerBonus,
    }]);
    setFacturarPickerQuery("");
  };

  const removeFacturarLine = (idx) => {
    setFacturarLines(prev => prev.filter((_, k) => k !== idx));
  };

  // Filter products by the picker query (codigo OR descripcion).
  const facturarPickerMatches = facturarPickerQuery.trim().length === 0 ? [] :
    products
      .filter(p => {
        const q = facturarPickerQuery.toLowerCase();
        return (p.codigo || "").toLowerCase().includes(q) ||
               (p.descripcion || "").toLowerCase().includes(q);
      })
      .slice(0, 8);

  const submitFacturar = async () => {
    if (!facturarOrder) return;
    const lineas = facturarLines
      .filter(l => parseFloat(l.cantidad) > 0)
      .map(l => ({
        codigo: l.codigo,
        cantidad: parseFloat(l.cantidad) || 0,
        producto: l.producto,
        precio_unitario: parseFloat(l.precio_unitario) || 0,
        es_bonificacion: !!l.es_bonificacion,
        iva_pct: l.es_bonificacion ? 0 : facturarIvaPct,
      }));
    if (lineas.length === 0) {
      toast.error("La factura debe tener al menos una línea con cantidad > 0");
      return;
    }
    setFacturarLoading(true);
    try {
      const { data } = await api.post(`/invoices/from-order/${facturarOrder.id}`, {
        lineas,
        iva_pct_global: facturarIvaPct,
        observaciones: facturarObs,
      });
      toast.success(`Factura ${data.numero} emitida — inventario descontado`);
      setFacturarOrder(null);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al facturar");
    } finally {
      setFacturarLoading(false);
    }
  };

  const facturarSubtotal = facturarLines.reduce((s, l) =>
    s + (parseFloat(l.cantidad) || 0) * (l.es_bonificacion ? 0 : (parseFloat(l.precio_unitario) || 0)), 0);
  const facturarIva = +(facturarSubtotal * (facturarIvaPct / 100)).toFixed(2);
  const facturarTotal = +(facturarSubtotal + facturarIva).toFixed(2);
  const [formData, setFormData] = useState({
    nombre_cliente: "",
    nombre_vendedor: "",
    cliente_id: "",
    lineas: []
  });
  // The Customer record selected from the catalog. Mirrors formData but kept
  // as a full object so the form can render the auto-filled (read-only) data
  // and surface a "cliente incompleto" warning when `phone` is missing.
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentLine, setCurrentLine] = useState({
    codigo: "",
    cantidad: 1,
    producto: "",
    precio_unitario: 0,
    subtotal: 0
  });
  const [aiImage, setAiImage] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [sellers, setSellers] = useState([]);
  // Preview modal — opened by clicking a row. Read-only with PDF / Print actions.
  const [previewOrder, setPreviewOrder] = useState(null);

  const openOrderPreview = (order) => setPreviewOrder(order);

  const downloadOrderPdf = (orderId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/pdf`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `pedido-${orderId.slice(0, 8)}.pdf`;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      })
      .catch(() => toast.error("Error al descargar PDF"));
  };

  const printOrderPdf = async (orderId) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/pdf`;
    const token = localStorage.getItem("token");
    try {
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const win = window.open(objectUrl, "_blank");
      if (win) {
        win.addEventListener("load", () => win.print(), { once: true });
      }
    } catch { toast.error("No se pudo abrir la impresión"); }
  };

  useEffect(() => {
    fetchOrders();
    fetchProducts();
    fetchCustomers();
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const response = await api.get("/sellers");
      setSellers(response.data || []);
    } catch (error) {
      // Sellers module is optional; silently ignore if endpoint is unavailable.
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get("/orders");
      setOrders(response.data);
    } catch (error) {
      toast.error("Error al cargar pedidos");
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get("/products");
      setProducts(response.data);
    } catch (error) {
      console.error("Error fetching products");
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers");
    }
  };

  /** Tap a product card → add 1 unit (or stack on existing line, re-applying
   *  bonificación rules from the catalog). Mirrors SellerOrderTake's POS UX. */
  const quickAddFromCatalog = (p, qty = 1) => {
    setFormData((prev) => {
      const lines = prev.lineas;
      const idx = lines.findIndex(
        (l) => (l.codigo || "").toLowerCase() === (p.codigo || "").toLowerCase() && !l.es_bonificacion,
      );
      if (idx >= 0) {
        const newQty = (lines[idx].cantidad || 0) + qty;
        const filtered = lines.filter(
          (l) => (l.codigo || "").toLowerCase() !== (p.codigo || "").toLowerCase(),
        );
        return { ...prev, lineas: [...filtered, ...buildPromoLines(p, newQty)] };
      }
      return { ...prev, lineas: [...lines, ...buildPromoLines(p, qty)] };
    });
  };

  /** Recompute one line's qty, re-applying bonificación rules from the catalog. */
  const recalcLineWithPromo = (lines, idx, newQty) => {
    const target = lines[idx];
    if (!target) return lines;
    const prod = products.find((x) => (x.codigo || "").toLowerCase() === (target.codigo || "").toLowerCase());
    const cleaned = lines.filter((l, i) => {
      if (i === idx) return false;
      if (i === idx + 1 && l.es_bonificacion && l.codigo === target.codigo) return false;
      return true;
    });
    const replacement = prod
      ? buildPromoLines(prod, newQty)
      : [{ ...target, cantidad: newQty, subtotal: (target.precio_unitario || 0) * newQty }];
    const adjusted = Math.min(idx, cleaned.length);
    return [...cleaned.slice(0, adjusted), ...replacement, ...cleaned.slice(adjusted)];
  };

  const setLineQty = (idx, qty) => {
    const cantidad = Math.max(1, parseInt(qty || 0, 10) || 1);
    setFormData((prev) => {
      const line = prev.lineas[idx];
      if (!line || line.es_bonificacion) return prev;
      return { ...prev, lineas: recalcLineWithPromo(prev.lineas, idx, cantidad) };
    });
  };

  const setLinePrice = (idx, price) => {
    const p = Math.max(0, parseFloat(price) || 0);
    setFormData((prev) => ({
      ...prev,
      lineas: prev.lineas.map((l, i) =>
        i === idx && !l.es_bonificacion
          ? { ...l, precio_unitario: p, precio_override: true, subtotal: +(p * (l.cantidad || 0)).toFixed(2) }
          : l,
      ),
    }));
  };

  const removeLineAt = (idx) => {
    setFormData((prev) => ({ ...prev, lineas: prev.lineas.filter((_, i) => i !== idx) }));
  };

  /** Pick a product from the autocomplete dropdown. */
  const pickProduct = (product) => {
    const subtotal = product.precio_venta * currentLine.cantidad;
    setCurrentLine({
      ...currentLine,
      codigo: product.codigo,
      producto: product.descripcion,
      precio_unitario: product.precio_venta,
      subtotal,
    });
    // Auto-focus quantity for fast entry: type product → tab/enter → quantity.
    setTimeout(() => {
      const q = document.querySelector('[data-testid="order-line-quantity-input"]');
      q?.focus();
      q?.select?.();
    }, 0);
  };

  const pickCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData((f) => ({
      ...f,
      cliente_id: customer.id,
      nombre_cliente: customer.nombre_facturacion,
      // Auto-fill seller from the customer's assignment so the user
      // doesn't have to retype it every time. Admin can still override.
      nombre_vendedor: customer.vendedor_asignado || f.nombre_vendedor,
    }));
  };

  const clearCustomer = () => {
    setSelectedCustomer(null);
    setFormData((f) => ({ ...f, cliente_id: "", nombre_cliente: "" }));
  };

  const handleCodigoChange = (codigo) => {
    // Check in-memory products first (the autocomplete list) — fast path.
    const local = products.find(
      (p) => (p.codigo || "").toLowerCase() === codigo.toLowerCase()
    );
    if (local) {
      const subtotal = local.precio_venta * currentLine.cantidad;
      setCurrentLine({
        ...currentLine,
        codigo,
        producto: local.descripcion,
        precio_unitario: local.precio_venta,
        subtotal,
      });
      return;
    }
    setCurrentLine({ ...currentLine, codigo, producto: "", precio_unitario: 0, subtotal: 0 });
  };

  const handleCantidadChange = (cantidad) => {
    const subtotal = currentLine.precio_unitario * cantidad;
    setCurrentLine({ ...currentLine, cantidad, subtotal });
  };

  const addLine = () => {
    if (!currentLine.codigo || currentLine.cantidad <= 0) {
      toast.error("Por favor ingresa un código y cantidad válidos");
      return;
    }
    // Apply discount + bonification rules if the product is in the catalog.
    const prod = products.find((p) => (p.codigo || "").toLowerCase() === (currentLine.codigo || "").toLowerCase());
    const promoLines = prod
      ? buildPromoLines(prod, currentLine.cantidad)
      : [{ ...currentLine, es_bonificacion: false }];
    setFormData({
      ...formData,
      lineas: [...formData.lineas, ...promoLines]
    });
    setCurrentLine({
      codigo: "",
      cantidad: 1,
      producto: "",
      precio_unitario: 0,
      subtotal: 0
    });
  };

  const removeLine = (index) => {
    setFormData({
      ...formData,
      lineas: formData.lineas.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_id || !selectedCustomer) {
      toast.error("Elegí un cliente del catálogo antes de crear el pedido");
      return;
    }
    if (!(selectedCustomer.phone || "").trim()) {
      toast.error("El cliente seleccionado no tiene teléfono — completá los datos primero");
      return;
    }
    if (formData.lineas.length === 0) {
      toast.error("Agrega al menos una línea al pedido");
      return;
    }
    try {
      // Backend authoritatively re-applies discount + bonification rules,
      // so we strip any pre-computed bonus lines before POSTing. Lines flagged
      // with `precio_override` keep their `precio_unitario` so the backend
      // honors the inline price edit.
      const sortedLines = [...formData.lineas]
        .filter((l) => !l.es_bonificacion)
        .sort((a, b) => a.codigo.localeCompare(b.codigo))
        .map(({ codigo, cantidad, precio_unitario, precio_override }) =>
          precio_override
            ? { codigo, cantidad, precio_unitario, precio_override: true }
            : { codigo, cantidad },
        );
      await api.post("/orders", { ...formData, lineas: sortedLines });
      toast.success("Pedido creado");
      setIsDialogOpen(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      const det = error?.response?.data?.detail;
      // Backend returns a structured error for the customer-completeness policy
      // (see /app/backend/routers/orders.py::validate_order_customer).
      if (det && typeof det === "object" && det.error) {
        toast.error(det.message || "No se pudo crear el pedido");
        return;
      }
      toast.error("Error al crear pedido");
    }
  };

  const resetForm = () => {
    setFormData({
      nombre_cliente: "",
      nombre_vendedor: "",
      cliente_id: "",
      lineas: []
    });
    setSelectedCustomer(null);
    setCurrentLine({
      codigo: "",
      cantidad: 1,
      producto: "",
      precio_unitario: 0,
      subtotal: 0
    });
  };

  const handleDelete = (id) => {
    setDeleteTarget({ type: 'single', id });
    setDeleteConfirmOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      toast.error("No hay pedidos seleccionados");
      return;
    }
    setDeleteTarget({ type: 'bulk' });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (deleteTarget?.type === 'single') {
        await api.delete(`/orders/${deleteTarget.id}`);
        toast.success("Pedido eliminado");
      } else if (deleteTarget?.type === 'bulk') {
        const response = await api.post('/orders/bulk-delete', selectedIds);
        toast.success(response.data.message);
        setSelectedIds([]);
      }
      setDeleteConfirmOpen(false);
      setDeleteTarget(null);
      fetchOrders();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === orders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(orders.map(o => o.id));
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setAiImage(file);
    }
  };

  const extractOrderFromImage = async () => {
    if (!aiImage) {
      toast.error("Por favor selecciona una imagen");
      return;
    }

    setAiLoading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        const formData = new FormData();
        formData.append("image_base64", base64String);

        try {
          const response = await api.post("/orders/extract-from-image", formData, {
            headers: { "Content-Type": "multipart/form-data" }
          });
          setExtractedData(response.data);
          toast.success("Información extraída. Revisa y aplica los datos.");
        } catch (error) {
          toast.error("Error al extraer información de la imagen");
        } finally {
          setAiLoading(false);
        }
      };
      reader.readAsDataURL(aiImage);
    } catch (error) {
      toast.error("Error al procesar imagen");
      setAiLoading(false);
    }
  };

  const applyExtractedData = async () => {
    if (!extractedData) return;

    const lineasConDetalles = [];
    for (const linea of extractedData.lineas || []) {
      try {
        const response = await api.get(`/products/${linea.codigo}`);
        const product = response.data;
        lineasConDetalles.push({
          codigo: linea.codigo,
          cantidad: linea.cantidad,
          producto: product.descripcion,
          precio_unitario: product.precio_venta,
          subtotal: product.precio_venta * linea.cantidad
        });
      } catch (error) {
        lineasConDetalles.push({
          codigo: linea.codigo,
          cantidad: linea.cantidad,
          producto: "",
          precio_unitario: 0,
          subtotal: 0
        });
      }
    }

    // Try to fuzzy-match the AI-extracted customer name against the catalog.
    // If we find one, pre-select it so the order is policy-compliant from
    // the start. If we don't, the user lands on the dialog without a
    // selected customer and has to pick one manually.
    const aiName = (extractedData.nombre_cliente || "").trim().toLowerCase();
    const matched = aiName
      ? customers.find(c =>
          (c.nombre_facturacion || "").toLowerCase() === aiName ||
          (c.nombre_fantasia || "").toLowerCase() === aiName,
        )
      : null;
    if (matched) {
      setSelectedCustomer(matched);
    } else {
      setSelectedCustomer(null);
    }
    setFormData({
      cliente_id: matched?.id || "",
      nombre_cliente: matched?.nombre_facturacion || "",
      nombre_vendedor: extractedData.nombre_vendedor || "",
      lineas: lineasConDetalles
    });
    if (aiName && !matched) {
      toast.warning(`No encontré "${extractedData.nombre_cliente}" en tu catálogo. Elegilo manualmente o creálo primero.`);
    }
    setIsAIDialogOpen(false);
    setIsDialogOpen(true);
    setExtractedData(null);
    setAiImage(null);
  };

  if (loading) {
    return <PageLoader label="Cargando pedidos…" testid="orders-loader" />;
  }

  const downloadBlob = async (url, filename) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("download failed");
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error("Error al descargar archivo");
    }
  };

  const downloadOrdersTemplate = (format = "csv") => {
    const url = format === "excel" ? "/api/orders/template/excel" : "/api/orders/template";
    const fname = format === "excel" ? "plantilla_pedidos.xlsx" : "plantilla_pedidos.csv";
    return downloadBlob(url, fname).then(() =>
      toast.success(`Plantilla ${format === "excel" ? "Excel" : "CSV"} descargada`),
    );
  };

  const exportOrders = (format, days) => {
    const fname = `pedidos_${days}dias.${format === "excel" ? "xlsx" : "pdf"}`;
    downloadBlob(`/api/orders/export/${format}?days=${days}`, fname).then(() =>
      toast.success(`Pedidos de ${days} días exportados (${format.toUpperCase()})`),
    );
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/orders/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data.message || "Pedidos importados");
      if (res.data.errors?.length) {
        toast.error(`${res.data.errors.length} fila(s) con error`);
      }
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar pedidos");
    }
  };

  return (
    <div data-testid="orders-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Pedidos
          </h2>
          <p className="text-slate-500 text-sm mt-1">Gestiona los pedidos de tu negocio</p>
        </div>
        <div className="flex gap-3">
          {selectedIds.length > 0 && (
            <Button
              onClick={handleBulkDelete}
              variant="outline"
              className="rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
              data-testid="bulk-delete-orders-button"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Eliminar ({selectedIds.length})
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid="download-orders-template-button">
                <Download className="h-4 w-4 mr-2" />
                Plantilla
                <ChevronDown className="h-3 w-3 ml-1.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem onClick={() => downloadOrdersTemplate("excel")} className="rounded-sm cursor-pointer" data-testid="download-orders-template-excel">
                <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadOrdersTemplate("csv")} className="rounded-sm cursor-pointer" data-testid="download-orders-template-csv">
                <FileText className="h-4 w-4 mr-2 text-slate-600" /> CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <label>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImportFile}
              className="hidden"
              id="orders-csv-import"
              data-testid="import-orders-input"
            />
            <Button
              type="button"
              variant="outline"
              className="rounded-sm"
              onClick={() => document.getElementById("orders-csv-import")?.click()}
              data-testid="import-orders-button"
            >
              <Upload className="h-4 w-4 mr-2" />
              Importar
            </Button>
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="rounded-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-orders-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Exportar pedidos
                <ChevronDown className="h-3.5 w-3.5 ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-64" data-testid="export-orders-menu">
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                PDF — Lista profesional
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[30, 60, 90].map((d) => (
                <DropdownMenuItem
                  key={`pdf-${d}`}
                  onClick={() => exportOrders("pdf", d)}
                  data-testid={`export-orders-pdf-${d}`}
                  className="rounded-sm cursor-pointer"
                >
                  <FileText className="h-4 w-4 mr-2 text-rose-600" />
                  Últimos {d} días
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-slate-500">
                Excel — Editable
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {[30, 60, 90].map((d) => (
                <DropdownMenuItem
                  key={`xlsx-${d}`}
                  onClick={() => exportOrders("excel", d)}
                  data-testid={`export-orders-excel-${d}`}
                  className="rounded-sm cursor-pointer"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-700" />
                  Últimos {d} días
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Dialog open={isAIDialogOpen} onOpenChange={(open) => {
              setIsAIDialogOpen(open);
              if (!open) {
                setAiImage(null);
                setExtractedData(null);
              }
            }}>
            <DialogTrigger asChild>
              <Button
                className="rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white border-0 shadow-sm"
                data-testid="ai-order-button"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Importar desde WhatsApp
                <Sparkles className="h-3.5 w-3.5 ml-2 opacity-80" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-[#25D366]" />
                  Importar pedido desde foto de WhatsApp
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">
                  <p className="font-medium text-slate-800 mb-1.5">Cómo funciona:</p>
                  <ol className="list-decimal list-inside space-y-1 text-slate-600">
                    <li>El vendedor toma la foto del pedido manuscrito y la envía por WhatsApp.</li>
                    <li>Guardás la imagen en tu computadora.</li>
                    <li>La subís aquí y la IA extrae cliente, vendedor y líneas.</li>
                  </ol>
                </div>
                {!aiImage ? (
                  <label
                    htmlFor="ai-image-upload"
                    className="block ai-dropzone p-8 rounded-sm text-center cursor-pointer hover:border-[#25D366] hover:bg-[#25D366]/5 transition-colors"
                    data-testid="ai-image-dropzone"
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="ai-image-upload"
                      data-testid="ai-image-input"
                    />
                    <ImagePlus className="h-12 w-12 mx-auto mb-3 text-[#25D366]" />
                    <p className="text-sm font-semibold text-slate-800 mb-1">
                      Subí la foto del pedido
                    </p>
                    <p className="text-xs text-slate-500">
                      Captura de WhatsApp, foto del cuaderno o cualquier imagen legible
                    </p>
                  </label>
                ) : (
                  <div className="relative border border-slate-200 rounded-sm p-3 bg-slate-50">
                    <button
                      type="button"
                      onClick={() => { setAiImage(null); setExtractedData(null); }}
                      className="absolute top-2 right-2 p-1 rounded-sm bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                      data-testid="ai-image-clear"
                      aria-label="Quitar imagen"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-white border border-slate-200">
                        <ImageIcon className="h-5 w-5 text-slate-400" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{aiImage.name}</p>
                        <p className="text-xs text-slate-500">
                          {(aiImage.size / 1024).toFixed(1)} KB · Listo para extraer
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {extractedData && (
                  <Card className="p-4 bg-emerald-50/50 border-emerald-200 rounded-sm">
                    <h4 className="font-semibold text-sm mb-2 flex items-center gap-1.5 text-emerald-900">
                      <Sparkles className="h-3.5 w-3.5" /> Datos extraídos
                    </h4>
                    <div className="text-sm space-y-1 text-slate-700">
                      <p><span className="text-slate-500">Cliente:</span> <strong>{extractedData.nombre_cliente || "No detectado"}</strong></p>
                      <p><span className="text-slate-500">Vendedor:</span> <strong>{extractedData.nombre_vendedor || "No detectado"}</strong></p>
                      <p><span className="text-slate-500">Líneas detectadas:</span> <strong>{extractedData.lineas?.length || 0}</strong></p>
                    </div>
                    {extractedData.lineas?.length > 0 && (
                      <div className="mt-3 border-t border-emerald-200 pt-2 max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-slate-500">
                              <th className="text-left py-1 font-medium">Código</th>
                              <th className="text-right py-1 font-medium">Cantidad</th>
                            </tr>
                          </thead>
                          <tbody>
                            {extractedData.lineas.map((l, i) => (
                              <tr key={i} className="border-t border-emerald-100">
                                <td className="py-1 mono">{l.codigo || "—"}</td>
                                <td className="py-1 text-right font-medium">{l.cantidad}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                )}
                <div className="flex gap-3">
                  <Button
                    onClick={extractOrderFromImage}
                    disabled={!aiImage || aiLoading}
                    className="flex-1 btn-primary rounded-sm"
                    data-testid="extract-order-button"
                  >
                    {aiLoading ? "Extrayendo…" : (extractedData ? "Reintentar" : "Extraer información")}
                  </Button>
                  {extractedData && (
                    <Button
                      onClick={applyExtractedData}
                      className="flex-1 rounded-sm bg-[#25D366] hover:bg-[#1ebe5d] text-white border-0"
                      data-testid="apply-extracted-data-button"
                    >
                      Crear pedido
                    </Button>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <OrderFormDialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}
            formData={formData}
            setFormData={setFormData}
            selectedCustomer={selectedCustomer}
            pickCustomer={pickCustomer}
            clearCustomer={clearCustomer}
            customers={customers}
            sellers={sellers}
            products={products}
            quickAddFromCatalog={quickAddFromCatalog}
            setLineQty={setLineQty}
            setLinePrice={setLinePrice}
            removeLineAt={removeLineAt}
            onSubmit={handleSubmit}
          />
        </div>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {orders.length === 0 ? (
          <EmptyState
            icon={ShoppingCart}
            title="Aún no hay pedidos"
            description="Crea un pedido manualmente o sube una foto y deja que la IA estructure las líneas automáticamente."
            actionLabel="Nuevo pedido"
            actionOnClick={() => { resetForm(); setIsDialogOpen(true); }}
            testid="orders-empty-state"
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="py-3 px-4 w-12">
                  <Checkbox
                    checked={orders.length > 0 && selectedIds.length === orders.length}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-orders-checkbox"
                  />
                </th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">N°</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Cliente</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Vendedor</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Líneas</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Total</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Estado</th>
                <th className="text-left py-3 px-4 font-medium text-slate-600">Fecha</th>
                <th className="text-right py-3 px-4 font-medium text-slate-600">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                  <tr
                    key={order.id}
                    className="table-row border-b border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors"
                    data-testid="order-row"
                    onClick={() => openOrderPreview(order)}
                  >
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(order.id)}
                        onCheckedChange={() => toggleSelect(order.id)}
                        data-testid={`select-order-${order.id}`}
                      />
                    </td>
                    <td className="py-3 px-4 mono text-xs text-slate-600">
                      {order.numero || <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-3 px-4 font-medium">{order.nombre_cliente}</td>
                    <td className="py-3 px-4">{order.nombre_vendedor}</td>
                    <td className="py-3 px-4 text-right">{order.lineas?.length || 0}</td>
                    <td className="py-3 px-4 text-right font-medium">{formatMoney(order.total)}</td>
                    <td className="py-3 px-4">
                      <OrderEstadoBadge estado={order.estado || "alistando"} />
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {new Date(order.created_at).toLocaleDateString('es-ES')}
                    </td>
                    <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <WhatsAppShareButton type="order" id={order.id} label="WA" size="sm" testid={`order-wa-${order.id}`} />
                        {(order.estado || "alistando") === "alistando" && (
                          <Button
                            data-testid={`facturar-order-${order.id}`}
                            onClick={() => openFacturarDialog(order)}
                            size="sm"
                            className="btn-primary rounded-sm h-8"
                            title="Convertir a factura (descuenta inventario)"
                          >
                            <Receipt className="h-3 w-3 mr-1" />Facturar
                          </Button>
                        )}
                        {(order.estado || "alistando") !== "facturada" && (
                          <Button
                            data-testid={`delete-order-${order.id}`}
                            onClick={() => handleDelete(order.id)}
                            variant="ghost"
                            size="sm"
                            className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        )}
      </Card>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmar eliminación?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === 'bulk'
                ? `Estás a punto de eliminar ${selectedIds.length} pedido(s). Esta acción no se puede deshacer.`
                : 'Estás a punto de eliminar este pedido. Esta acción no se puede deshacer.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-order-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-order-button"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Order preview modal — clicking any row opens this. Read-only,
         shows full ticket + PDF / Print actions. */}
      <Dialog open={!!previewOrder} onOpenChange={(o) => !o && setPreviewOrder(null)}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-[rgb(0,47,167)]" />
              Pedido {previewOrder?.numero ? `${previewOrder.numero} — ` : ""}{previewOrder?.nombre_cliente}
              {previewOrder && <OrderEstadoBadge estado={previewOrder.estado || "alistando"} />}
            </DialogTitle>
          </DialogHeader>
          {previewOrder && (
            <div className="mt-2 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Cliente</p><p className="font-medium">{previewOrder.nombre_cliente}</p>{previewOrder.cliente_telefono && <p className="text-slate-500">📞 {previewOrder.cliente_telefono}</p>}{previewOrder.cliente_direccion && <p className="text-slate-500">{previewOrder.cliente_direccion}</p>}</div>
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Vendedor</p><p className="font-medium">{previewOrder.nombre_vendedor}</p>{previewOrder.codigo_vendedor && <p className="text-slate-500 mono">{previewOrder.codigo_vendedor}</p>}</div>
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Fecha</p><p className="font-medium">{previewOrder.created_at ? new Date(previewOrder.created_at).toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" }) : "-"}</p></div>
                <div><p className="text-slate-500 uppercase tracking-wider font-bold">Líneas</p><p className="font-medium">{previewOrder.lineas?.length || 0} producto(s)</p></div>
              </div>
              <div className="overflow-x-auto max-h-72 border border-slate-200 rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr><th className="text-left py-2 px-2">Código</th><th className="text-left py-2 px-2">Producto</th><th className="text-right py-2 px-2">Cant.</th><th className="text-right py-2 px-2">Precio</th><th className="text-right py-2 px-2">Subtotal</th></tr></thead>
                  <tbody>
                    {(previewOrder.lineas || []).map((l, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${l.es_bonificacion ? "bg-emerald-50/40" : ""}`}>
                        <td className="py-1.5 px-2 mono">{l.codigo}</td>
                        <td className="py-1.5 px-2">{l.producto}{l.es_bonificacion && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">GRATIS</span>}</td>
                        <td className="py-1.5 px-2 text-right">{l.cantidad}</td>
                        <td className="py-1.5 px-2 text-right">{formatMoney(l.precio_unitario)}</td>
                        <td className="py-1.5 px-2 text-right font-medium">{formatMoney(l.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr><td colSpan={4} className="py-2 px-2 text-right font-bold uppercase tracking-wider border-t border-slate-200">Total</td><td className="py-2 px-2 text-right font-black text-base text-[rgb(0,47,167)] border-t border-slate-200">{formatMoney(previewOrder.total)}</td></tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => printOrderPdf(previewOrder.id)} className="rounded-sm" data-testid="preview-order-print">
                  <FileText className="h-4 w-4 mr-2" />Imprimir
                </Button>
                <Button type="button" onClick={() => downloadOrderPdf(previewOrder.id)} className="btn-primary rounded-sm" data-testid="preview-order-pdf">
                  <Download className="h-4 w-4 mr-2" />Descargar PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Facturar (Order → Invoice) dialog. The admin can edit each line's
         qty/price before issuing — useful when bodega encuentra menos stock
         del solicitado por el vendedor. Bonus lines (es_bonificacion) are
         kept but always charged at price 0 and IVA 0%. */}
      <Dialog open={!!facturarOrder} onOpenChange={(o) => !o && setFacturarOrder(null)}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[rgb(0,47,167)]" />
              Facturar pedido {facturarOrder?.numero || ""} — {facturarOrder?.nombre_cliente}
            </DialogTitle>
          </DialogHeader>
          {facturarOrder && (
            <div className="mt-2 space-y-3">
              <p className="text-xs text-slate-500">
                Editá las cantidades, precios, o agregá y quitá líneas si la bodega encontró diferencias.
                Al emitir, se descuenta del inventario y el pedido pasa a <strong>facturada</strong>.
              </p>
              <div className="overflow-x-auto max-h-72 border border-slate-200 rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left py-2 px-2 font-medium">Código</th>
                      <th className="text-left py-2 px-2 font-medium">Producto</th>
                      <th className="text-right py-2 px-2 font-medium w-24">Cantidad</th>
                      <th className="text-right py-2 px-2 font-medium w-32">Precio unit.</th>
                      <th className="text-right py-2 px-2 font-medium w-24">Subt.</th>
                      <th className="py-2 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturarLines.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-6 text-center text-slate-400 text-xs" data-testid="facturar-empty">
                          No hay líneas. Agregá productos abajo.
                        </td>
                      </tr>
                    )}
                    {facturarLines.map((l, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${l.es_bonificacion ? "bg-emerald-50/40" : ""}`} data-testid={`facturar-line-row-${i}`}>
                        <td className="py-1.5 px-2 mono">{l.codigo}</td>
                        <td className="py-1.5 px-2">
                          {l.producto}
                          {l.es_bonificacion && <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1 py-0.5 rounded-sm bg-emerald-100 text-emerald-800 border border-emerald-200">GRATIS</span>}
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" min="0" step="any" value={l.cantidad}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFacturarLines(prev => prev.map((x, k) => k === i ? { ...x, cantidad: v } : x));
                            }}
                            className="h-7 rounded-sm text-right mono w-20"
                            data-testid={`facturar-line-qty-${i}`} />
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          <Input type="number" min="0" step="any" value={l.es_bonificacion ? 0 : l.precio_unitario}
                            disabled={l.es_bonificacion}
                            onChange={(e) => {
                              const v = e.target.value;
                              setFacturarLines(prev => prev.map((x, k) => k === i ? { ...x, precio_unitario: v } : x));
                            }}
                            className="h-7 rounded-sm text-right mono w-24 disabled:bg-slate-50"
                            data-testid={`facturar-line-price-${i}`} />
                        </td>
                        <td className="py-1.5 px-2 text-right font-medium">
                          {formatMoney((parseFloat(l.cantidad) || 0) * (l.es_bonificacion ? 0 : (parseFloat(l.precio_unitario) || 0)))}
                        </td>
                        <td className="py-1.5 px-1 text-center">
                          <Button
                            type="button" variant="ghost" size="sm"
                            onClick={() => removeFacturarLine(i)}
                            className="h-6 w-6 p-0 rounded-sm text-red-600 hover:bg-red-50"
                            title="Quitar línea"
                            data-testid={`facturar-line-remove-${i}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add-line picker */}
              <div className="border border-dashed border-slate-300 rounded-sm p-3 bg-slate-50/50" data-testid="facturar-add-line-section">
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="h-3.5 w-3.5 text-[rgb(0,47,167)]" />
                  <p className="text-xs font-semibold text-slate-700">Agregar línea</p>
                  <label className="flex items-center gap-1 text-[11px] text-slate-600 ml-auto cursor-pointer">
                    <input
                      type="checkbox"
                      checked={facturarPickerBonus}
                      onChange={(e) => setFacturarPickerBonus(e.target.checked)}
                      className="h-3 w-3 rounded-sm accent-emerald-600"
                      data-testid="facturar-picker-bonus-toggle"
                    />
                    <span>Bonificación (gratis)</span>
                  </label>
                </div>
                <div className="relative">
                  <Input
                    value={facturarPickerQuery}
                    onChange={(e) => setFacturarPickerQuery(e.target.value)}
                    placeholder="Buscar por código o descripción…"
                    className="rounded-sm h-8 text-xs"
                    data-testid="facturar-picker-input"
                  />
                  {facturarPickerMatches.length > 0 && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg max-h-56 overflow-y-auto" data-testid="facturar-picker-dropdown">
                      {facturarPickerMatches.map(p => (
                        <button
                          key={p.id || p.codigo}
                          type="button"
                          onClick={() => addFacturarLine(p)}
                          className="w-full text-left px-3 py-1.5 hover:bg-blue-50 border-b border-slate-100 last:border-0"
                          data-testid={`facturar-picker-option-${p.codigo}`}
                        >
                          <div className="flex justify-between items-center gap-2">
                            <span className="mono text-[11px] text-slate-500">{p.codigo}</span>
                            <span className="text-[11px] text-slate-700 mono">
                              {formatMoney(p.precio_venta || 0)}
                            </span>
                          </div>
                          <div className="text-xs text-slate-800 truncate">{p.descripcion}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {facturarPickerQuery.trim().length > 0 && facturarPickerMatches.length === 0 && (
                    <div className="mt-1 text-[11px] text-slate-500">Sin coincidencias.</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">IVA aplicable (%)</label>
                  <Input type="number" min="0" max="100" step="any" value={facturarIvaPct}
                    onChange={(e) => setFacturarIvaPct(parseFloat(e.target.value) || 0)}
                    className="rounded-sm" data-testid="facturar-iva-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Observaciones</label>
                  <Input value={facturarObs} onChange={(e) => setFacturarObs(e.target.value)} className="rounded-sm" data-testid="facturar-obs-input" />
                </div>
              </div>
              <div className="border-t border-slate-200 pt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="mono">{formatMoney(facturarSubtotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">IVA {facturarIvaPct}%</span><span className="mono">{formatMoney(facturarIva)}</span></div>
                <div className="flex justify-between text-lg font-black"><span className="uppercase tracking-wider">Total</span><span className="mono text-[rgb(0,47,167)]" data-testid="facturar-total">{formatMoney(facturarTotal)}</span></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setFacturarOrder(null)} className="rounded-sm" disabled={facturarLoading}>Cancelar</Button>
                <Button type="button" onClick={submitFacturar} className="btn-primary rounded-sm" disabled={facturarLoading || facturarLines.length === 0} data-testid="facturar-submit-button">
                  <Receipt className="h-4 w-4 mr-2" />{facturarLoading ? "Emitiendo…" : "Emitir factura"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Visual badge for the order's lifecycle state.
function OrderEstadoBadge({ estado }) {
  const map = {
    alistando:  { label: "Alistando", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    facturada:  { label: "Facturada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    cancelada:  { label: "Cancelada", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  };
  const m = map[estado] || map.alistando;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${m.cls}`}>
      {m.label}
    </span>
  );
}

